import {
  Banner,
  Button,
  EmptyState,
  FlexBox,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import {
  AiSettingsVersionFragment,
  useAiSettingsVersionQuery,
  useAiSettingsVersionsQuery,
} from 'lib-knowledge-management-common/graphql';
import Page from 'lib-knowledge-utils/components/page/index';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useNavigatePrompt } from 'hooks/useNavigatePrompt';

import styles from './aiSuggestionsSettings.module.scss';
import {
  DraftWritingFields,
  GapDetectionFields,
} from './components/aiSettingsFields';
import { VersionActions } from './components/versionActions';
import {
  AiSettingsVersionSummary,
  VersionSwitcher,
} from './components/versionSwitcher';
import { useAiSettingsForm } from './hooks/useAiSettingsForm';
import { useAiSettingsLiveUpdates } from './hooks/useAiSettingsLiveUpdates';
import { useUpdateAiSettingsDraft } from './hooks/useAiSettingsMutations';
import { useCanEditAiSettings } from './hooks/useCanEditAiSettings';
import { useEditVersion } from './hooks/useEditVersion';
import {
  AiSettingsFormValues,
  toDraftVariables,
  toFormValues,
} from './utils/aiSettingsForm';
import { draftVariables } from './utils/aiSettingsQueries';

/**
 * The AI article suggestions settings: the draft, editable, or one saved
 * version, read-only, chosen by the URL as in the MIM settings.
 */
export function AiSuggestionsSettingsPage(): JSX.Element {
  const { versionId } = useParams<{ versionId: string }>();
  useAiSettingsLiveUpdates();

  // Read on the version pages too: editing a version needs to know whether
  // the draft holds changes it would replace.
  const draftQuery = useAiSettingsVersionQuery({
    variables: draftVariables,
    fetchPolicy: 'cache-and-network',
  });
  const versionQuery = useAiSettingsVersionQuery({
    variables: { id: versionId },
    skip: !versionId,
    fetchPolicy: 'cache-and-network',
  });
  const versionsQuery = useAiSettingsVersionsQuery({
    fetchPolicy: 'cache-and-network',
  });

  const shown = versionId
    ? versionQuery.data?.aiSettingsVersion
    : draftQuery.data?.aiSettingsVersion;
  const error = versionId ? versionQuery.error : draftQuery.error;

  if (!shown) {
    return (
      <Page.Section>
        {error ? (
          <EmptyState
            title="Something went wrong"
            description="The AI suggestions settings could not be loaded."
            buttonProps={{
              children: 'Try again',
              onClick: () =>
                void (versionId ? versionQuery : draftQuery).refetch(),
            }}
          />
        ) : (
          <FlexBox justifyContent="center" padding="500">
            <Spinner />
          </FlexBox>
        )}
      </Page.Section>
    );
  }

  return (
    // A fresh form per record, so one never starts out with another's edits.
    <AiSuggestionsSettingsForm
      key={versionId ?? 'draft'}
      shown={shown}
      versionId={versionId}
      draft={draftQuery.data?.aiSettingsVersion ?? undefined}
      versions={versionsQuery.data?.aiSettingsVersions ?? []}
      versionsLoading={versionsQuery.loading}
    />
  );
}

function AiSuggestionsSettingsForm({
  shown,
  versionId,
  draft,
  versions,
  versionsLoading,
}: {
  shown: AiSettingsVersionFragment;
  versionId: string | undefined;
  draft: AiSettingsVersionFragment | undefined;
  versions: AiSettingsVersionSummary[];
  versionsLoading: boolean;
}) {
  const isVersion = !!versionId;
  const { canEdit, loaded: accessLoaded } = useCanEditAiSettings();
  // Saved versions never change; the draft only for agents who may edit it.
  const readOnly = isVersion || !canEdit;
  // Without a draft, the draft query stands in the latest version or the
  // defaults, and there is nothing a new version would add.
  const hasDraft = !!draft?.isDraft;
  const liveVersion = versions.find((version) => version.isActive);
  const viewedVersion = versions.find((version) => version.id === versionId);

  const { successNotify, errorNotify } = useNotification();
  const [updateDraft, { loading: saving }] = useUpdateAiSettingsDraft();
  const editVersion = useEditVersion({ version: shown, hasDraft });

  const serverValues = useMemo(() => toFormValues(shown), [shown]);
  const { form, latestValues, loadLatest } = useAiSettingsForm(serverValues);
  const { control, handleSubmit, reset } = form;
  // Read up front: the form only tracks what a render has asked for, and a
  // short-circuited `isValid` would never be kept up to date.
  const { isDirty, isValid } = form.formState;
  const hasUnsavedChanges = !readOnly && isDirty;

  useNavigatePrompt(hasUnsavedChanges);

  const onSave = async (values: AiSettingsFormValues) => {
    try {
      const { data } = await updateDraft({
        variables: toDraftVariables(values),
      });
      const saved = data?.organisationUpdateAiSettingsDraft;
      // What was saved, trimmed as the backend stores it.
      reset(saved ? toFormValues(saved) : values);
      successNotify({
        title: 'Draft saved',
        text: 'Create a version and make it live for the AI to use it.',
      });
    } catch {
      errorNotify({ text: 'The draft could not be saved. Try again.' });
    }
  };

  const banner = isVersion ? (
    <Banner
      variant="info"
      icon="info"
      description={
        <Text.Paragraph as="span" variant="3">
          You are viewing a saved version. It is read-only.
        </Text.Paragraph>
      }
      actionSlot={
        canEdit ? (
          <Button onClick={editVersion}>Edit this version</Button>
        ) : undefined
      }
    />
  ) : accessLoaded && !canEdit ? (
    <Banner
      variant="info"
      icon="info"
      description={
        <Text.Paragraph as="span" variant="3">
          You can view these settings, but you need permission to manage
          knowledge settings to change them.
        </Text.Paragraph>
      }
    />
  ) : latestValues ? (
    <Banner
      variant="warning"
      headline="Someone else changed this draft"
      description={
        <Text.Paragraph as="span" variant="3">
          Load their changes to keep working from the latest draft. This
          discards your unsaved changes. If you save instead, your changes
          replace theirs.
        </Text.Paragraph>
      }
      actionSlot={
        <Button variant="secondary" onClick={loadLatest}>
          Load latest
        </Button>
      }
    />
  ) : undefined;

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Page.Section
        title="AI article suggestions"
        controls={
          <>
            <VersionSwitcher
              versions={versions}
              loading={versionsLoading}
              currentVersionId={versionId}
            />
            {canEdit && (
              <VersionActions
                viewedVersion={viewedVersion}
                readOnly={isVersion}
                hasDraft={hasDraft}
                hasUnsavedChanges={hasUnsavedChanges}
              />
            )}
          </>
        }
        // The intro goes in the callout rather than the body: the body keeps a
        // minimum height that leaves a gap under a single paragraph.
        callout={
          <FlexBox flexDirection="column" gap="300">
            {banner}
            <Text.Paragraph>
              Fine-tune how the AI spots gaps in your knowledge base and writes
              article suggestions. Changes are saved to a draft. The AI keeps
              using the live version until you create a version from the draft
              and make it live.
              {!versionsLoading &&
                !liveVersion &&
                ' No version is live yet, so the AI uses the default settings.'}
            </Text.Paragraph>
          </FlexBox>
        }
      />

      <fieldset disabled={readOnly} className={styles.fieldset}>
        <Page.Section title="Finding knowledge gaps">
          <Text.Paragraph>
            When a conversation closes, the AI looks for other conversations
            about the same issue. Once it finds enough, it checks whether your
            knowledge base covers the issue, and suggests a new or updated
            article if it does not.
          </Text.Paragraph>
          <GapDetectionFields control={control} disabled={readOnly} />
        </Page.Section>

        <Page.Section title="Writing drafts">
          <DraftWritingFields control={control} disabled={readOnly} />
        </Page.Section>
      </fieldset>

      {!readOnly && (
        <Page.Section>
          <FlexBox justifyContent="flex-end" gap="200">
            <Button
              type="button"
              variant="tertiary"
              disabled={!isDirty || saving}
              onClick={() => reset()}
            >
              Discard changes
            </Button>
            <Button
              type="submit"
              isLoading={saving}
              loadingIndicator="Saving..."
              disabled={!isDirty || !isValid || saving}
            >
              Save draft
            </Button>
          </FlexBox>
        </Page.Section>
      )}
    </form>
  );
}
